import type React from "react";
import { useTranslation } from "react-i18next";
import type { MappingResultRow } from "@models/mappingResult";
import { MAPPING_STATUS_BADGE } from "@utils/resultStatusBadges";
import StatusBadge from "./StatusBadge";

interface RowFindingBadgeProps {
  row: MappingResultRow;
}

/**
 * What the PIPELINE concluded about a row, as a badge — or nothing.
 *
 * Both tables render this under the suggestion it judges: technical-manual
 * under the catalogued value, wd-mapping under the suggested ItemMP text. A
 * finding is always a claim about one cell's contents, and in the Status column
 * it read as a claim about the row.
 *
 * It is a component rather than three lines repeated per table because those
 * three lines drifted: the size, the two translation keys and the "render
 * nothing when there is no finding" rule are one decision, and TM kept the old
 * badge size for a while after wd-mapping shrank it. Here the two tables
 * cannot disagree.
 *
 * `null` for `mapped`, `no_item_found` and `failed` — see `MAPPING_STATUS_BADGE`
 * for why each says nothing. A clean row shows the value alone.
 */
const RowFindingBadge: React.FC<RowFindingBadgeProps> = ({ row }) => {
  const { t } = useTranslation();
  const spec = MAPPING_STATUS_BADGE[row.mappingStatus];
  if (!spec) return null;

  return (
    <StatusBadge
      size="sm"
      spec={spec}
      text={t(spec.badgeKey, { defaultValue: spec.badge })}
      label={t(spec.labelKey, { defaultValue: spec.fallback })}
    />
  );
};

export default RowFindingBadge;
